import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useOfferStore } from '@/lib/store';
import {
  mapPdfImportToOfferState,
  type PdfImportApiData,
} from '@shared/pdf-import-contract';
import { FileText, Upload, Loader2, CheckCircle, AlertCircle, FileDown, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

export default function PDFImport() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<PdfImportApiData | null>(null);
  const [dragActive, setDragActive] = useState(false);
  // Vorgabe: an — bisheriges Verhalten (Preise werden übernommen) bleibt der Normalfall.
  const [importPrices, setImportPrices] = useState(true);

  const replaceWithPdfImport = useOfferStore((state) => state.replaceWithPdfImport);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file: File) => {
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      toast({
        title: 'Ungültiger Dateityp',
        description: 'Bitte wählen Sie eine PDF-Datei aus.',
        variant: 'destructive',
      });
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/import-pdf', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Import fehlgeschlagen');
      }

      setImportResult(result.data);

      const warnCount = result.data.warnings?.length ?? 0;
      toast({
        title: warnCount > 0 ? 'PDF analysiert — bitte prüfen' : 'PDF analysiert',
        description: warnCount > 0
          ? `${result.data.positionCount} Positionen gefunden, ${warnCount} Hinweis(e) — Import unten prüfen.`
          : `${result.data.positionCount} Positionen gefunden`,
        variant: warnCount > 0 ? 'destructive' : undefined,
      });

    } catch (error) {
      console.error('PDF import error:', error);
      toast({
        title: 'Import fehlgeschlagen',
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportToQuote = () => {
    if (!importResult) return;

    const draft = mapPdfImportToOfferState(importResult, {
      // Leer: Die Nummer kommt aus dem Nummernkreis (der Store fordert sie
      // direkt nach replaceWithPdfImport an). Hier stand frueher
      // `IMP-<Zufall>` -- keine Angebotsnummer, sah aber wie eine aus.
      fallbackOfferNumber: '',
      fallbackDate: new Date().toISOString().split('T')[0],
    }, { includePrices: importPrices });
    replaceWithPdfImport(draft);

    toast({
      title: 'Import erfolgreich',
      description: `${draft.quoteItems.length} Positionen wurden importiert`,
    });

    setIsOpen(false);
    setImportResult(null);
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return '-';
    return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  };

  // Weichensteller-Transparenz: der Nutzer MUSS erkennen, welcher Weg lief und wie
  // zuverlässig das Ergebnis ist — sonst ist ein 30-von-1350-Import unsichtbar.
  const renderMethodBanner = (result: PdfImportApiData) => {
    const verification = result.verification;
    const pathLabel = result.method === 'deterministic'
      ? 'Strukturbasiert exakt gelesen (ohne KI)'
      : result.method === 'vision'
        ? 'KI-Auswertung (Bilderkennung)'
        : 'KI-Auswertung (Textanalyse)';

    let verdict: string;
    let exact = false;
    if (verification?.verified && verification.deviationCents === 0) {
      exact = true;
      verdict = `${verification.positionCount} Positionen gelesen — die Summe (${formatCurrency(verification.positionsTotalNet)}) stimmt auf den Cent mit der Dokumentsumme überein.`;
    } else if (verification?.verified) {
      verdict = `${verification.positionCount} Positionen gelesen — Summe weicht um ${formatCurrency((verification.deviationCents ?? 0) / 100)} von der Dokumentsumme (${formatCurrency(verification.documentedTotal)}) ab (innerhalb Toleranz). Bitte prüfen.`;
    } else if (verification?.failureReason === 'no_documented_total') {
      verdict = `${verification.positionCount} Positionen gelesen — im Dokument wurde keine eigene Schlusssumme gefunden, die Vollständigkeit konnte NICHT geprüft werden. Bitte mit dem PDF abgleichen.`;
    } else if (verification) {
      verdict = `${verification.positionCount} Positionen gelesen — Summe (${formatCurrency(verification.comparableTotal)}) weicht von der Dokumentsumme (${formatCurrency(verification.documentedTotal)}) ab. Bitte mit dem PDF abgleichen.`;
    } else {
      verdict = `${result.positionCount} Positionen gelesen — ohne Summenabgleich.`;
    }

    return (
      <div
        className={`rounded-md border p-3 text-sm ${
          exact
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
            : 'border-slate-300 bg-slate-50 text-slate-800'
        }`}
      >
        <div className="flex items-center gap-2 font-medium">
          {exact ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {pathLabel}
        </div>
        <p className="mt-1">{verdict}</p>
      </div>
    );
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setIsOpen(true)}
        className="h-8 text-xs gap-1"
      >
        <FileText className="h-3.5 w-3.5" />
        PDF Import
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              PDF Angebot/Rechnung importieren
            </DialogTitle>
            <DialogDescription>
              Laden Sie eine PDF-Datei hoch. Strukturierte Leistungsverzeichnisse werden
              exakt gelesen und gegen die Dokumentsumme geprüft; sonst extrahiert die KI
              die Positionen.
            </DialogDescription>
          </DialogHeader>

          {!importResult ? (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {isImporting ? (
                <div className="space-y-4">
                  <Loader2 className="h-12 w-12 mx-auto animate-spin text-blue-600" />
                  <p className="text-gray-600">PDF wird analysiert...</p>
                  <p className="text-sm text-gray-400">Die KI extrahiert Positionen und Metadaten</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="h-12 w-12 mx-auto text-gray-400" />
                  <div>
                    <p className="text-gray-600">PDF-Datei hier ablegen</p>
                    <p className="text-sm text-gray-400">oder klicken zum Auswählen</p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileInput}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label htmlFor="pdf-upload">
                    <Button variant="outline" asChild>
                      <span>Datei auswählen</span>
                    </Button>
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Weg + Verifikation: welcher Pfad lief und ob die Summe belegt ist. */}
              {renderMethodBanner(importResult)}
              {/* Warn-Banner: Teil-Import/Summen-Abweichung darf NICHT still bleiben. */}
              {importResult.warnings && importResult.warnings.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="flex items-center gap-2 font-medium">
                    <AlertCircle className="h-4 w-4" />
                    Bitte prüfen — der Import ist möglicherweise unvollständig
                  </div>
                  <ul className="mt-2 list-disc space-y-1 pl-6">
                    {importResult.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Metadata Card */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">Erkannte Daten</CardTitle>
                    <Badge variant={importResult.documentType === 'angebot' ? 'default' : 'secondary'}>
                      {importResult.documentType === 'angebot' ? 'Angebot' :
                       importResult.documentType === 'rechnung' ? 'Rechnung' : 'Dokument'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  {importResult.metadata.documentNumber && (
                    <div>
                      <span className="text-gray-500">Nummer:</span>
                      <span className="ml-2 font-medium">{importResult.metadata.documentNumber}</span>
                    </div>
                  )}
                  {importResult.metadata.date && (
                    <div>
                      <span className="text-gray-500">Datum:</span>
                      <span className="ml-2 font-medium">{importResult.metadata.date}</span>
                    </div>
                  )}
                  {importResult.metadata.customerName && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Kunde:</span>
                      <span className="ml-2 font-medium">{importResult.metadata.customerName}</span>
                    </div>
                  )}
                  {importResult.metadata.projectName && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Projekt:</span>
                      <span className="ml-2 font-medium">{importResult.metadata.projectName}</span>
                    </div>
                  )}
                  {/* Netto/Brutto sind Summen aus dem Quelldokument — bei abgewähltem Preisimport
                      dürfen sie nicht suggerieren, dass diese Preise mit übernommen werden. */}
                  {importPrices && importResult.metadata.totalNet !== undefined && (
                    <div>
                      <span className="text-gray-500">Netto:</span>
                      <span className="ml-2 font-medium">{formatCurrency(importResult.metadata.totalNet)}</span>
                    </div>
                  )}
                  {importPrices && importResult.metadata.totalGross !== undefined && (
                    <div>
                      <span className="text-gray-500">Brutto:</span>
                      <span className="ml-2 font-medium">{formatCurrency(importResult.metadata.totalGross)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Preisimport-Kästchen */}
              <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                <Checkbox
                  id="pdf-import-prices"
                  checked={importPrices}
                  onCheckedChange={(checked) => setImportPrices(checked === true)}
                />
                <Label htmlFor="pdf-import-prices" className="text-sm cursor-pointer">
                  Preise mit importieren
                </Label>
                {!importPrices && (
                  <span className="ml-auto text-xs text-amber-700">
                    Positionen, Mengen und Texte werden übernommen — Preise bleiben leer.
                  </span>
                )}
              </div>

              {/* Positions Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Positionen ({importResult.positions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Pos</TableHead>
                          <TableHead>Beschreibung</TableHead>
                          <TableHead className="w-20 text-right">Menge</TableHead>
                          <TableHead className="w-16">Einheit</TableHead>
                          <TableHead className="w-24 text-right">EP</TableHead>
                          <TableHead className="w-24 text-right">GP</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importResult.positions.map((pos, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-mono text-xs">
                              {pos.positionNumber || index + 1}
                            </TableCell>
                            <TableCell className="max-w-xs truncate text-xs">
                              {pos.beschreibung}
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {pos.menge.toLocaleString('de-DE')}
                            </TableCell>
                            <TableCell className="text-xs">{pos.einheit}</TableCell>
                            <TableCell className="text-right text-xs">
                              {importPrices ? formatCurrency(pos.einzelpreis) : '–'}
                            </TableCell>
                            <TableCell className="text-right text-xs font-medium">
                              {!importPrices ? '–' : pos.isBedarfsposition ? 'E.P.' : formatCurrency(pos.gesamtpreis)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setImportResult(null)}
                >
                  <X className="h-4 w-4 mr-2" />
                  Andere Datei
                </Button>
                <Button
                  onClick={handleImportToQuote}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  In Angebot übernehmen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
